import React, { Fragment, useEffect, useState } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import DOMPurify from "dompurify";

import "./PageNavigationItem.scss";

import {
  FaCheck,
  FaCloudUploadAlt,
  FaRegFileAlt,
  FaListUl,
  FaMapMarkerAlt,
  FaSpinner,
} from "react-icons/fa";
import { FiAlertTriangle } from "react-icons/fi";

//@todo refactor into shape strategy
//return component based on schema values
const getSymbol = (symbol) => {
  if (symbol === "document") {
    return <FaRegFileAlt size={"1.2em"} />;
  } else if (symbol === "upload") {
    return <FaCloudUploadAlt size={"1.2em"} />;
  } else if (symbol === "list") {
    return <FaListUl size={"1.2em"} />;
  } else if (symbol === "map") {
    return <FaMapMarkerAlt size={"1em"} />;
  } else if (symbol === "loader") {
    return <FaSpinner size={"1.2em"} className={"icon-spin"} />;
  }
};

function PageNavigationItem({
  symbol,
  prefix,
  name,
  index,
  active,
  error,
  complete,
  classes,
  handlePageChange,
  keyboardFocusProps: { navRef, setNavFocus, setFormFocus },
}) {
  const [showIcon, setShowIcon] = useState(false);

  // Tracks keyboard focus thoroughly
  useEffect(() => {
    if (active) {
      setNavFocus();
    }
  }, [active]);

  const onHover = () => {
    setShowIcon(true);
  };

  const unHover = () => {
    setShowIcon(false);
  };

  const showActive = active;
  const showError = error;
  const showComplete = complete;
  const showDefault = !complete && !active && !error;

  let pageItemClasses = classNames({
    "page-item-title--active": showActive,
    "page-item-title--error": showError && !showActive,
    "page-item-title--complete": showComplete && !showActive,
    "page-item-title": showDefault,
  });

  let circleItemClasses = classNames({
    "circle-active flex-justify-center flex-align-center": showActive,
    "circle-error flex-justify-center flex-align-center":
      (showError && !showActive && showIcon) ||
      (showError && showActive && showIcon),
    "circle-complete": showComplete && !showActive && !showIcon,
    "circle-complete-hover flex-justify-center flex-align-center":
      (showComplete && !showActive && showIcon) ||
      (showComplete && showActive && showIcon),
    "circle flex-justify-center flex-align-center": showDefault,
  });

  return (
    <li
      data-index={index}
      tabIndex={0}
      className="page-item grid-row flex-row flex-align-center"
      onKeyUp={(e) => {
        if (e.key === " " || e.key === "Tab") handlePageChange();
      }}
      onClick={handlePageChange}
      onMouseOver={onHover}
      onMouseLeave={unHover}
      // Sets ref for focus, make forms keyboard navigable
      ref={active ? navRef : null}
      onKeyPress={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          typeof setFormFocus === "function" && setFormFocus();
          // Prevent disorienting spacebar scrolling firing on onKeyDown
          e.preventDefault();
        }
      }}
      // Removes event ordering conflict between onMouseDown and blur for onClick
      onMouseDown={(e) => {
        e.preventDefault();
      }}
    >
      <span className="divider"> </span>
      <div className="grid-col flex-2">
        {!showActive && showError ? (
          !showIcon ? (
            <div className="triangle-warning">
              <FiAlertTriangle
                style={{ style: "light" }}
                fill={"white"}
                size={"3.5em"}
              />
            </div>
          ) : (
            <div className={circleItemClasses}>
              {symbol ? (
                <span>{getSymbol(symbol)}</span>
              ) : (
                <div
                  className={classes}
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(prefix),
                  }}
                />
              )}
            </div>
          )
        ) : (
          <Fragment>
            {!showActive && showComplete ? (
              !showIcon ? (
                <div className={circleItemClasses}>
                  <FaCheck size={"1.5em"} />
                </div>
              ) : (
                <div className={circleItemClasses}>
                  {symbol ? (
                    <span>{getSymbol(symbol)}</span>
                  ) : (
                    <div
                      className={classes}
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(prefix),
                      }}
                    />
                  )}
                </div>
              )
            ) : symbol ? (
              <div className={circleItemClasses}>{getSymbol(symbol)}</div>
            ) : (
              <div className={circleItemClasses}>
                <div
                  className={classes}
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(prefix),
                  }}
                />
              </div>
            )}
          </Fragment>
        )}
      </div>
      <div className="grid-col flex-10">
        <h3
          className={pageItemClasses}
          dangerouslySetInnerHTML={{ __html: name }}
        />
      </div>
    </li>
  );
}

PageNavigationItem.defaultProps = {
  index: 0,
  active: false,
  complete: false,
  name: "Default Label",
};

PageNavigationItem.propTypes = {
  index: PropTypes.number,
  active: PropTypes.bool,
  complete: PropTypes.bool,
  name: PropTypes.string,
};

export default PageNavigationItem;
